import { useCallback } from 'react'

import { getCurrency } from '@common/utils/currency.ts'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import EditIcon from '@/assets/icons/icon-edit.svg'
import NoColorIcon from '@/assets/icons/icon-nocolor.svg'
import IconPlus from '@/assets/icons/icon-plus.svg'
import RemoveIcon from '@/assets/icons/icon-trash.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table/table.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption'
import type { AddColorBody } from '@/modules/display-settings/service/inbox-indication-color-service.ts'

import { useDisplaySettingsMutations } from '../../hooks/use-display-settings-mutations.tsx'
import type { ColorWithIndex, ColorWithPrice } from '../../utils'

export const FanSpendColorTable = () => {
  const toast = useToast()

  const { addColorMutation, removeColorMutation, editColorMutation } =
    useDisplaySettingsMutations()

  const { control } = useFormContext()

  const queryClient = useQueryClient()

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'colors',
    keyName: 'uuid',
  })

  const handleAddSpendColor = useCallback(
    (newColor: AddColorBody) => {
      addColorMutation.mutateAsync(newColor, {
        onSuccess: () => {
          append(newColor)
          queryClient.invalidateQueries('settings')
          toast.success('Successfully added new Fan Spend Color')
        },
      })
    },
    [addColorMutation, append, queryClient, toast],
  )

  const handleUpdateSpendColor = useCallback(
    async (index: number, color: Partial<ColorWithPrice>) => {
      const requestBody = {
        color: color.colorHex,
        backgroundColor: color.backgroundColorHex,
        price: color.price,
        id: color.id,
      }

      await editColorMutation.mutateAsync(requestBody, {
        onSuccess: () => {
          update(index, color)
          queryClient.invalidateQueries('settings')
          toast.success('Successfully edited Fan Spend Color')
        },
      })
    },
    [editColorMutation, queryClient, toast, update],
  )

  const handleColorEdit = useCallback(
    (row: ColorWithIndex) => {
      openDialog({
        type: DIALOG_TYPE.FAN_SPEND_COLOR,
        mode: 'edit',
        onAddColor: handleAddSpendColor,
        onUpdateColor: handleUpdateSpendColor,
        defaultValues: row,
      })
    },
    [handleAddSpendColor, handleUpdateSpendColor],
  )

  const handleColorDelete = useCallback(
    ({ index, color }: { index: number; color: ColorWithPrice }) => {
      openDialog({
        type: DIALOG_TYPE.DELETE_FAN_SPEND_COLOR,
        onDelete: () => {
          removeColorMutation.mutateAsync(color.id, {
            onSuccess: () => {
              remove(index)
              queryClient.invalidateQueries('settings')
              toast.success('Successfully removed Fan Spend Color')
            },
          })
        },
      })
    },
    [queryClient, remove, removeColorMutation, toast],
  )

  const handleColorAdd = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.FAN_SPEND_COLOR,
      mode: 'add',
      onAddColor: handleAddSpendColor,
      onUpdateColor: handleUpdateSpendColor,
    })
  }, [handleAddSpendColor, handleUpdateSpendColor])

  return (
    <div className="flex flex-col">
      <SettingsOption
        title="Fan Spend Colors"
        disableTopPadding
        description={
          <div className="flex items-center gap-1">
            <span>
              Highlight your fans in different colors depending on their spend
            </span>
          </div>
        }
      >
        <Button
          className="flex gap-1 text-sm"
          onClick={handleColorAdd}
        >
          <IconPlus
            width="20"
            height="20"
          />
          <span className="hidden truncate md:block">Add Fan Color</span>
          <span className="truncate md:hidden">Fan Color</span>
        </Button>
      </SettingsOption>

      <div className="bg-card rounded-xl p-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Spend</TableHead>
              <TableHead>Color</TableHead>
              <TableHead>Background</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
            {fields.map((field: any, index: number) => (
              <TableRow key={field.id}>
                <TableCell>{getCurrency(field.price)}</TableCell>

                <TableCell>
                  <div
                    className="h-6 w-6 rounded-full border"
                    style={{ backgroundColor: field.colorHex }}
                  />
                </TableCell>

                <TableCell>
                  {!field.backgroundColorHex ? (
                    <NoColorIcon />
                  ) : (
                    <div
                      className="h-6 w-6 rounded-full border"
                      style={{ backgroundColor: field.backgroundColorHex }}
                    />
                  )}
                </TableCell>

                <TableCell className="float-right">
                  <div className="flex items-center gap-3">
                    <EditIcon
                      type="button"
                      className="text-primary-500 cursor-pointer"
                      onClick={() => handleColorEdit({ index, ...field })}
                      width={18}
                      height={18}
                    />
                    {index === 0 ? null : (
                      <RemoveIcon
                        width={18}
                        height={18}
                        type="button"
                        className="text-danger-default cursor-pointer"
                        onClick={() =>
                          handleColorDelete({
                            index,
                            color: field,
                          })
                        }
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
